import { useMemo } from 'react';

import cn from 'classnames';
import { motion } from 'framer-motion';

import { STAMP_CARD_WIDGET_STAMP_EARNED } from '../../../constants';

import {
  JUST_STAMPED_CASCADE_STEP_SECONDS,
  makeStampVariants,
  STAMP_ENTER_SECONDS,
  STAMP_JUST_EARNED_EXTRA_DELAY_SECONDS,
  STAMP_STAGGER_BASE_DELAY_SECONDS,
  STAMP_STAGGER_STEP_SECONDS,
} from './stampAnimation';
import styles from './StampCardStampsRow.module.scss';

interface StampCardStampIconProps {
  staggerIndex: number;
  isEarned: boolean;
  emptyStampAssetUrl?: string | null;
  filledStampAssetUrl?: string | null;
  isFree?: boolean;
  freeStampAssetUrl?: string | null;
  locked?: boolean;
  lockedStampAssetUrl?: string | null;
  isJustStamped?: boolean;
  justStampedOrder?: number;
  isJustUnlocked?: boolean;
  animated: boolean;
  animateState: 'hidden' | 'visible' | 'exit';
  staggerStepSeconds?: number;
  dataTestId?: string;
}

export function StampCardStampIcon(props: StampCardStampIconProps) {
  const {
    staggerIndex,
    isEarned,
    emptyStampAssetUrl,
    filledStampAssetUrl,
    isFree,
    freeStampAssetUrl,
    locked,
    lockedStampAssetUrl,
    isJustStamped,
    justStampedOrder = -1,
    isJustUnlocked,
    animated,
    animateState,
    staggerStepSeconds = STAMP_STAGGER_STEP_SECONDS,
    dataTestId,
  } = props;

  const variants = useMemo(() => makeStampVariants(staggerStepSeconds), [staggerStepSeconds]);

  const revealDelaySeconds =
    (animated
      ? STAMP_STAGGER_BASE_DELAY_SECONDS +
        staggerIndex * staggerStepSeconds +
        STAMP_ENTER_SECONDS +
        STAMP_JUST_EARNED_EXTRA_DELAY_SECONDS
      : 0) +
    Math.max(justStampedOrder, 0) * JUST_STAMPED_CASCADE_STEP_SECONDS;

  const iconElement = (
    <>
      {emptyStampAssetUrl && <img alt="" className={styles.stampBaseIcon} src={emptyStampAssetUrl} />}
      {isEarned && (
        <img
          alt=""
          className={cn(styles.stampFilledIcon, isJustStamped && styles.stampJustEarnedIcon)}
          src={(isFree && freeStampAssetUrl) || filledStampAssetUrl || STAMP_CARD_WIDGET_STAMP_EARNED}
          style={isJustStamped ? { animationDelay: `${revealDelaySeconds}s` } : undefined}
        />
      )}
      {(locked || isJustUnlocked) && lockedStampAssetUrl && (
        <img
          alt=""
          className={cn(styles.stampLockedIcon, isJustUnlocked && styles.stampJustUnlockedIcon)}
          src={lockedStampAssetUrl}
        />
      )}
    </>
  );

  if (!animated) {
    return (
      <div className={styles.stamp} data-testid={dataTestId}>
        {iconElement}
      </div>
    );
  }

  return (
    <motion.div
      animate={animateState}
      className={styles.stamp}
      custom={staggerIndex}
      data-testid={dataTestId}
      initial="hidden"
      variants={variants}
    >
      {iconElement}
    </motion.div>
  );
}
